import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../services/api';
import { Invoice, Contract, ContratoServicos } from '../../types';
import AdicionarItemNfcomModal from './AdicionarItemNfcomModal';
import EscritorioSelect from '../selects/EscritorioSelect';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import { formatarValorOnInput, formatarValor, formatarData, formatarDataHora } from '../../utils/formatToView';
import escritorioService from '../../services/escritorioService';
import nfcomService from '../../services/nfcomService';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import { useNavigate } from 'react-router-dom';

export type NfcomItem = {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  cclass?: string;
  desconto?: number;
  valor_total: number;
  valor_unitario_formatado?: string;
  desconto_formatado?: string;
  valor_total_formatado?: string;
  plano_nome?: string;
};

interface EmitirNfcomModalProps {
  open: boolean;
  onClose: () => void;
  faturaId: number | null;
  onSuccess?: () => void;
}

const EmitirNfcomModal: React.FC<EmitirNfcomModalProps> = ({ open, onClose, faturaId, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [contrato, setContrato] = useState<Contract | null>(null);
  const [servicos, setServicos] = useState<ContratoServicos[] | null>(null);
  const [itens, setItens] = useState<NfcomItem[]>([]);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [codigoBarras, setCodigoBarras] = useState<string>('');
  const [pixCopiaCola, setPixCopiaCola] = useState<string>('');
  const [finalidade, setFinalidade] = useState<'normal' | 'substituicao' | 'ajuste'>('normal');
  const [formaEmissao, setFormaEmissao] = useState<'normal' | 'contingencia'>('normal');
  const [descricaoTemplate, setDescricaoTemplate] = useState<'manter_existente' | 'servico_comunicacao_multimidia' | 'servico_comunicacao_multimidia_plano' | 'provimento_internet' | 'titulo_plano'>('manter_existente');
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [cclass, setCclass] = useState<string>('0100401');
  const getDataEmissaoPadrao = () => {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [dataEmissao, setDataEmissao] = useState<string>(() => getDataEmissaoPadrao());
  const navigate = useNavigate();
  const parsearMoeda = (valorFormatado: string): number => {
    const numeroLimpo = (valorFormatado || '').toString().replace(/[^\d,]/g, '');
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  const formatInicial = (num: number): string => {
    try {
      const base = Number(num || 0).toFixed(2).replace('.', ',');
      return formatarValorOnInput(base);
    } catch {
      return formatarValorOnInput(String(num || 0));
    }
  };

  const handleEscritorioChange = async (value: number | number[] | null) => {
    const id = (value as number) || null;
    setEscritorioId(id);
    if (typeof id === 'number') {
      try {
        const resp = await escritorioService.getEscritorioById(id);
        const esc: any = (resp as any)?.data || resp;
        if (esc) {
          const desc = esc.descricao_padrao;
          const valid = new Set([
            'manter_existente',
            'servico_comunicacao_multimidia',
            'servico_comunicacao_multimidia_plano',
            'provimento_internet',
            'titulo_plano'
          ]);
          if (typeof desc === 'string' && valid.has(desc) && (desc as string).trim().length > 0) {
            setDescricaoTemplate(desc as any);
          } else {
            setDescricaoTemplate('manter_existente');
          }
        }
      } catch (e) {
        // silencioso
      }
    } else {
      // Limpar campos quando não houver escritório selecionado      
      setDescricaoTemplate('manter_existente');
    }
  };

  useEffect(() => {
    if (!open || !faturaId) return;

    const carregarDados = async () => {
      try {
        setLoading(true);
        // Buscar fatura completa
        const resp = await api.get(`/invoices/${faturaId}`);
        const inv = (resp as any).data?.data || (resp as any).data || resp;
        setInvoice(inv);
        // Pré-preencher código de barras e PIX, se disponíveis na fatura
        setCodigoBarras(inv?.linha_digitavel || '');
        setPixCopiaCola(inv?.qr_code_pix || '');
        setContrato(inv?.contrato || null);
        setEscritorioId((inv as any)?.escritorio_id ?? (inv as any)?.cliente?.escritorio_id ?? null);

        // Se houver contrato, buscar serviços do contrato
        if (inv?.contrato?.id) {
          const serv = await api.get(`/contratos/${inv.contrato.id}/servicos`);
          const raw = serv as any;
          const list = Array.isArray(raw?.data?.data)
            ? raw.data.data
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw)
                ? raw
                : [];
          // Opcional: filtrar somente serviços não cancelados
          const ativos = list.filter((s: any) => (s?.status || 'ativo') !== 'cancelado');
          setServicos(ativos);
        } else {
          setServicos([]);
        }
      } catch (err) {
        console.error('Erro ao carregar dados da fatura:', err);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [open, faturaId]);

  useEffect(() => {
    // Montar itens padrão a partir dos serviços do contrato; se não houver, criar item fictício
    if (!invoice) return;

    if (servicos && servicos.length > 0) {
      const list = servicos.map((s, idx) => {
        const valorUnitario = Number(s.valor || 0);
        const desconto = Number(s.desconto || 0);
        const quantidade = 1;
        const total = quantidade * valorUnitario - desconto;
        return {
          descricao: invoice.descricao,
          cclass: s.cclass_fiscal,
          quantidade,
          valor_unitario: valorUnitario,
          desconto,
          valor_total: total < 0 ? 0 : Number(total.toFixed(2)),
          valor_unitario_formatado: formatInicial(valorUnitario),
          desconto_formatado: formatInicial(desconto),
          valor_total_formatado: formatInicial(total < 0 ? 0 : Number(total.toFixed(2))),
          plano_nome: (s as any)?.plano?.nome
        } as NfcomItem;
      });
      setItens(list);
    } else {
      const valorUnitario = Number(invoice.valor || 0);
      const acrescimo = Number(invoice.acrescimo || 0);
      const desconto = Number(invoice.desconto || 0);
      const total = valorUnitario + acrescimo - desconto;
      setItens([
        {
          descricao: invoice.descricao || 'Serviço de comunicação',
          cclass: '0100401',
          quantidade: 1,
          valor_unitario: valorUnitario,
          desconto,
          valor_total: total < 0 ? 0 : Number(total.toFixed(2)),
          valor_unitario_formatado: formatInicial(valorUnitario),
          desconto_formatado: formatInicial(desconto),
          valor_total_formatado: formatInicial(total < 0 ? 0 : Number(total.toFixed(2))),
          plano_nome: undefined
        }
      ]);
    }
  }, [servicos, invoice]);

  useEffect(() => {
    // Atualizar a descrição dos itens com base no template selecionado
    setItens(prev => prev.map((it) => {
      let novaDescricao = it.descricao;
      const plano = it.plano_nome;
      if (descricaoTemplate === 'manter_existente') {
        novaDescricao = invoice?.descricao || it.descricao;
      } else if (descricaoTemplate === 'servico_comunicacao_multimidia') {
        novaDescricao = 'Serviço de Comunicação Multimidia';
      } else if (descricaoTemplate === 'provimento_internet') {
        novaDescricao = 'Provimento de Internet';
      } else if (descricaoTemplate === 'servico_comunicacao_multimidia_plano') {
        novaDescricao = `Serviço de Comunicação Multimidia - ${plano}`;
      } else if (descricaoTemplate === 'titulo_plano') {
        novaDescricao = plano || (invoice?.descricao || it.descricao);
      }
      return { ...it, descricao: novaDescricao };
    }));
  }, [descricaoTemplate]);

  const valorTotal = useMemo(() => {
    return itens.reduce((sum, item) => sum + Number(item.valor_total || 0), 0);
  }, [itens]);

  const valorTotalDesconto = useMemo(() => {
    return itens.reduce((sum, item) => sum + Number(item.desconto || 0), 0);
  }, [itens]);

  const handleAddItem = () => {
    setAddItemOpen(true);
  };

  const handleRemoveItem = (idx: number) => {
    setItens(prev => prev.filter((_, i) => i !== idx));
  };

  const handleChangeItem = (idx: number, field: keyof NfcomItem, value: any) => {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it } as NfcomItem;
      if (field === 'descricao') {
        (updated as any).descricao = value;
      } else if (field === 'valor_unitario') {
        const masked = formatarValorOnInput(String(value));
        updated.valor_unitario_formatado = masked;
        updated.valor_unitario = parsearMoeda(masked);
      } else if (field === 'desconto') {
        const masked = formatarValorOnInput(String(value));
        updated.desconto_formatado = masked;
        updated.desconto = parsearMoeda(masked);
      } else if (field === 'quantidade') {
        updated.quantidade = Number(value) || 0;
      } else if (field === 'cclass') {
        updated.cclass = value;
      }
      const qtd = Number(updated.quantidade || 0);
      const unit = Number(updated.valor_unitario || 0);
      const desc = Number(updated.desconto || 0);
      const total = qtd * unit - desc;
      updated.valor_total = total < 0 ? 0 : Number(total.toFixed(2));
      updated.valor_total_formatado = formatInicial(updated.valor_total);
      return updated;
    }));
  };

  const handleEmitir = async () => {
    if (!invoice) return;
    if (!dataEmissao || !String(dataEmissao).trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Data de emissão',
        text: 'Informe a data de emissão da NFCom.',
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
      });
      return;
    }
    const dtCheck = new Date(`${dataEmissao}T12:00:00`);
    if (Number.isNaN(dtCheck.getTime())) {
      await Swal.fire({
        icon: 'error',
        title: 'Data inválida',
        text: 'A data de emissão informada é inválida.',
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
      });
      return;
    }
    try {
      setLoading(true);
      // Persistir nota com itens para emissão (enviamos fatura_id e depois controller/serviço usam)
      // Aqui apenas chamamos o endpoint existente de emissão com os dados organizados.
      const itensLimpos = itens.map(it => ({
        descricao: it.descricao,
        cclass: it.cclass,
        quantidade: it.quantidade,
        valor_unitario: it.valor_unitario,
        desconto: it.desconto || 0,
        valor_total: it.valor_total
      }));

      const payload: any = {
        fatura_id: invoice.id,
        data_emissao: dataEmissao,
        itens: itensLimpos,
        codigo_barras: codigoBarras || undefined,
        pix_copia_cola: pixCopiaCola || undefined,
        finalidade: finalidade || undefined,
        forma: formaEmissao || undefined,
        escritorio_id: escritorioId || undefined
      };

      // Não enviamos gateway_id: backend utilizará o gateway padrão (is_default)
      Swal.fire({
        title: 'Aguarde',
        html: 'Emitindo NFCom...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: {
          popup: 'swal-zindex',
          container: 'swal-zindex'
        }
      });
      const resp = await api.post('/notas-nfcom/emitir', payload);
      const r: any = resp;
      const success = r?.success;
      const message = r?.message;
      Swal.close();
      await Swal.fire({
        icon: success ? 'success' : 'error',
        title: success ? 'Sucesso' : 'Erro',
        text: message,
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });
      if (success && onSuccess) onSuccess();
      navigate('/notas-nfcom'); //redireciona para a página de notas NFCom
      //handleClose();
    } catch (err: any) {
      console.error('Erro ao emitir NFCom:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erro ao emitir NFCom';
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: msg,
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setItens([]);
    setAddItemOpen(false);
    setCodigoBarras('');
    setPixCopiaCola('');
    setFinalidade('normal');
    setFormaEmissao('normal');
    setDescricaoTemplate('manter_existente');
    setDataEmissao(getDataEmissaoPadrao());
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile} className="swal-zindex" style={{ zIndex: 1300 }}>
      <DialogContent>
        {/* Quadro com informações do boleto/fatura */}
        {invoice && (
          <Box sx={{
            mt: 1,
            mb: 1.5,
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50'
          }}>
            <Typography variant="subtitle2" gutterBottom>Informações do Boleto</Typography>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2">{invoice.status}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Data de Vencimento</Typography>
                <Typography variant="body2">{invoice.data_vencimento ? formatarData(invoice.data_vencimento) : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Valor</Typography>
                <Typography variant="body2">{formatarValor((Number(invoice.valor) || 0) + (Number(invoice.acrescimo) || 0) - (Number(invoice.desconto) || 0))}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Desconto</Typography>
                <Typography variant="body2">{formatarValor(Number(invoice.desconto || 0))}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Data de Pagamento</Typography>
                <Typography variant="body2">{invoice.data_pagamento ? formatarDataHora(invoice.data_pagamento) : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="caption" color="text.secondary">Valor Pago</Typography>
                <Typography variant="body2">{formatarValor(Number((invoice as any).valor_pago || 0))}</Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Data de emissão"
                type="date"
                value={dataEmissao}
                onChange={(e) => setDataEmissao(e.target.value)}
                fullWidth
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <EscritorioSelect
                value={escritorioId}
                onChange={handleEscritorioChange}
                label="Escritório"
                size="small"
                onlyActive
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>Descrição dos itens</InputLabel>
                <Select
                  value={descricaoTemplate}
                  label="Descrição dos itens"
                  onChange={(e) => setDescricaoTemplate(e.target.value as any)}
                >
                  <MenuItem value={'manter_existente'}>Manter a descrição existente do boleto</MenuItem>
                  <MenuItem value={'servico_comunicacao_multimidia'}>Serviço de Comunicação Multimidia</MenuItem>
                  <MenuItem value={'servico_comunicacao_multimidia_plano'}>Serviço de Comunicação Multimidia - {(contrato as any)?.plano?.nome || '{plano}'}</MenuItem>
                  <MenuItem value={'provimento_internet'}>Provimento de Internet</MenuItem>
                  <MenuItem value={'titulo_plano'}>Titulo do plano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ my: 0.5, mt: 0 }} />
        <Box sx={{ mt: 0 }}>
          <Box sx={{
            p: 0.5,
            mb: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={20}></TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell width={100}>cClasse</TableCell>
                  <TableCell width={60}>Qtd.</TableCell>
                  <TableCell width={100}>Valor Unit.</TableCell>
                  <TableCell width={100}>Desconto</TableCell>
                  <TableCell width={120}>Total</TableCell>
                  <TableCell width={32} align="center" sx={{ p: 0 }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleAddItem()}
                      size="small"
                      sx={{ p: '2px' }}
                    >
                      <AddIcon fontSize="small" sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ py: 0, px: 0.5 }} align="center">
                      <Typography variant="body2" sx={{ fontSize: '0.90rem' }}>{idx + 1}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.descricao}
                        onChange={(e) => handleChangeItem(idx, 'descricao', e.target.value)}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, px: 0.5, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px 4px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }}>
                      <TextField
                        size="small"
                        value={item.cclass}
                        onChange={(e) => handleChangeItem(idx, 'cclass', e.target.value)}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, px: 0.5, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }}>
                      <TextField
                        size="small"
                        value={item.quantidade}
                        onChange={(e) => handleChangeItem(idx, 'quantidade', e.target.value)}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px 4px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }}>
                      <TextField
                        size="small"
                        value={item.valor_unitario_formatado ?? formatInicial(item.valor_unitario)}
                        onChange={(e) => handleChangeItem(idx, 'valor_unitario', e.target.value)}
                        InputProps={{
                          startAdornment: <span style={{ marginLeft: -8, marginRight: 1, fontSize: '0.90rem' }}>R$</span>
                        }}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }}>
                      <TextField
                        size="small"
                        value={item.desconto_formatado ?? formatInicial(item.desconto || 0)}
                        onChange={(e) => handleChangeItem(idx, 'desconto', e.target.value)}
                        InputProps={{
                          startAdornment: <span style={{ marginLeft: -8, marginRight: 1, fontSize: '0.90rem' }}>R$</span>
                        }}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }}>
                      <TextField
                        size="small"
                        value={item.valor_total_formatado ?? formatInicial(item.valor_total)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <span style={{ marginLeft: -8, marginRight: 1, fontSize: '0.90rem' }}>R$</span>
                        }}
                        sx={{
                          m: 0,
                          p: 0,
                          '& .MuiInputBase-root': { py: 0, fontSize: '0.90rem', minHeight: 28, height: 28 },
                          '& .MuiInputBase-input': { py: 0, px: 1, height: 20 }
                        }}
                        inputProps={{ style: { padding: '2px', height: 20 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 0.5 }} align="center">
                      <IconButton color="error" onClick={() => handleRemoveItem(idx)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1 }}>
              <Typography variant="body2">Valor total desconto: <b>{formatarValor(valorTotalDesconto)}</b></Typography>
              <Typography variant="body2">Valor total da nota: <b>{formatarValor(valorTotal)}</b></Typography>
            </Box>
          </Box>

          {invoice && (() => {
            const faturaTotalNum = (Number(invoice.valor) || 0) + (Number(invoice.acrescimo) || 0) - (Number(invoice.desconto) || 0);
            const diverge = Math.abs(valorTotal - faturaTotalNum) > 0.009;
            return diverge ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Valor da nota ({formatarValor(valorTotal)}) difere do valor da fatura ({formatarValor(faturaTotalNum)}).
              </Alert>
            ) : null;
          })()}

          <Divider sx={{ my: 1.5 }} />
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Finalidade</InputLabel>
                <Select
                  value={finalidade}
                  label="Finalidade"
                  onChange={(e) => setFinalidade(e.target.value as any)}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="substituicao">Substituição</MenuItem>
                  <MenuItem value="ajuste">Ajuste</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma</InputLabel>
                <Select
                  value={formaEmissao}
                  label="Forma"
                  onChange={(e) => setFormaEmissao(e.target.value as any)}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="contingencia">Contingência</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Código de barras do boleto (opcional)"
                value={codigoBarras}
                onChange={(e) => setCodigoBarras(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PIX copia e cola (opcional)"
                value={pixCopiaCola}
                onChange={(e) => setPixCopiaCola(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleEmitir} disabled={loading || !invoice}>Emitir Nota</Button>
      </DialogActions>
      <AdicionarItemNfcomModal
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        onAdd={(novo) => setItens(prev => [...prev, novo])}
      />
    </Dialog>
  );
};

export default EmitirNfcomModal;
